<template>
  <div class="editor">
    <div class="rounded-lg editor-menu-bar">
      <div class="menubar pt-3 pl-4 pr-4 space-x-5 text-sm py-2 flex justify-start text-brand-brown bg-brand-sand rounded-t-lg">
        <button class="" :class="{ 'is-active': editor.isActive('bold') }" @click="editor.commands.toggleBold">
          <font-awesome-icon :icon="['fas', 'bold']" />
        </button>

        <button class="" :class="{ 'is-active': editor.isActive('italic') }" @click="editor.commands.toggleItalic">
          <font-awesome-icon :icon="['fas', 'italic']" />
        </button>

        <!-- <button class="" :class="{ 'is-active': editor.isActive('underline') }" @click="editor.commands.toggleUnderline">
          <font-awesome-icon :icon="['fas', 'underline']" />
        </button> -->

        <!-- <button class="" :class="{ 'is-active': editor.isActive('strike') }" @click="editor.commands.toggleStrike">
          <font-awesome-icon :icon="['fas', 'strikethrough']" />
        </button> -->

        <!-- <button class="" :class="{ 'is-active': editor.isActive('paragraph') }" @click="editor.commands.setParagraph">
          <font-awesome-icon :icon="['fas', 'paragraph']" />
        </button> -->

        <button class="" :class="{ 'is-active': editor.isActive('bulletList') }" @click="editor.commands.toggleBulletList">
          <font-awesome-icon :icon="['fas', 'list-ul']" />
        </button>

        <button class="" :class="{ 'is-active': editor.isActive('orderedList') }" @click="editor.commands.toggleOrderedList">
          <font-awesome-icon :icon="['fas', 'list-ol']" />
        </button>

        <button v-if="editor.isActive('link')" :class="{ 'is-active': editor.isActive('link') }" @click="editor.chain().focus().unsetLink().run()">
          <font-awesome-icon :icon="['fas', 'unlink']" />
        </button>

        <button v-if="!editor.isActive('link')" :class="{ 'is-active': editor.isActive('link') }" @click="showLinkPrompt">
          <font-awesome-icon :icon="['fas', 'link']" />
        </button>

        <!-- insert image -->
        <button class="" @click="showImagePrompt">
          <font-awesome-icon :icon="['fas', 'image']" />
        </button>

        <!-- insert HTML -->
        <button class="" @click="showHtmlPrompt">
          <font-awesome-icon :icon="['fas', 'code']" />
        </button>

        <!-- insert video-->
        <button class="" @click="showVideoPrompt">
          <font-awesome-icon :icon="['fas', 'play-circle']" />
        </button>

        <!-- push last toolbar item to the right -->
        <div class="flex-grow"></div>

        <!-- merge fields -->
        <merge-field-dropdown :options="mergeFieldDropdownOptions" :command="insertMergeField"></merge-field-dropdown>

        <!--
        <button class="" @click="editor.commands.undo">
          <font-awesome-icon :icon="['fas', 'undo-alt']" />
        </button>

        <button class="" @click="editor.commands.redo">
          <font-awesome-icon :icon="['fas', 'redo-alt']" />
        </button>
        -->
      </div>
    </div>
    <!-- /menubar -->

    <div class="el-textarea el-input--4 border-brand-sand border-2 rounded-b-lg">
      <div class="el-textarea__inner font-semibold border-none">
        <editor-content class="mt-1" :editor="editor" />
        <span class="el-input__count" v-if="maxlength > 0">{{ length }}/{{ maxlength }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'; // required for this.$prompt
import { mapGetters } from 'vuex';
import { Editor, EditorContent } from '@tiptap/vue-2';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Youtube from '@tiptap/extension-youtube';
import MergeField from '@/components/UI/Editor/MergeField/MergeFieldExtension';
import MergeFieldDropdown from '@/components/UI/Editor/MergeField/MergeFieldDropdown.vue';
import { imagePrompt, videoPrompt, htmlPrompt, linkPrompt } from '@/components/UI/Editor/utils';
import { InsertHtml } from '@/components/UI/Editor/InsertHtml';

export default {
  name: 'Editor',
  props: {
    placeholder: String,
    maxlength: {
      type: Number,
      default: 0,
    },
  },
  components: {
    EditorContent,
    MergeFieldDropdown,
  },
  data() {
    return {
      editor: new Editor({
        extensions: [
          StarterKit,
          InsertHtml,
          Image,
          MergeField,
          Link.configure({
            openOnClick: false,
            defaultTarget: '_blank',
            HTMLAttributes: {
              class: 'text-brand-blue underline font-semibold',
            },
          }),
          Youtube.configure({
            width: '100%',
            modestBranding: true, // hide youtube logo from control bar
          }),
        ],
        content: this.placeholder,
        editorProps: {
          handleTextInput: (view) => {
            if (this.maxlength && view.state.doc.textContent.length >= this.maxlength && view.state.selection.empty) {
              return true;
            }

            return false;
          },
          handlePaste: (view, event, slice) => {
            if (this.maxlength && view.state.doc.textContent.length + slice.size > this.maxlength) {
              slice.content = new DocumentFragment();
            }
          },
        },
      }),
      length: 0,
    };
  },
  watch: {
    // allowing external sources to update the contents of the editor:
    placeholder(value) {
      const isSame = value === this.editor.getHTML();
      if (isSame) return;
      // console.log('%c>>> Updating tiptap content!', 'background-color: yellow; font-weight: bold;')
      this.editor.commands.setContent(value, false);
    },
  },
  computed: {
    ...mapGetters(['cancelFlowMergeFields', 'provider']),
    defaultMergeFields() {
      // CUSTOMER_NAME is not supported in Braintree:
      return this.cancelFlowMergeFields.filter((d) => {
        if (this.provider === 'paddle' && d.value === 'CUSTOMER_NAME') {
          return false;
        }
        return true;
      });
    },
    customAttributes() {
      const state = this.$store.state;
      const customAttributes = state.org?.customAttributes || [];
      return customAttributes.map((d) => ({
        label: d.label,
        id: d.name,
      }));
    },
    mergeFieldDropdownOptions() {
      return [
        {
          label: 'Custom Attributes',
          items: this.customAttributes,
        },
        {
          label: 'Default',
          items: this.defaultMergeFields,
        },
      ];
    },
  },

  created() {
    // Set the length based on the textContent from ProseMirror
    this.length = this.editor.state.doc.textContent.length;

    // Emit generated HTML back up to the parent component
    this.editor.on('update', ({ editor }) => {
      this.relayContents();
      this.length = editor.getText().length;
    });

    // Emit on load
    this.relayContents(false);
  },

  methods: {
    relayContents(sendUpdate = true) {
      if (sendUpdate) {
        this.$emit('update', this.editor.getHTML());
      }
      this.$emit('jsonUpdate', this.editor.getJSON());
    },
    focusEditor() {
      this.$nextTick(() => {
        this.editor.chain().focus().run();
        // this.editor.chain().focus('end').run();
        // this.editor.commands.selectNodeForward();
        // this.editor.commands.selectTextblockEnd();
      });
    },
    showImagePrompt() {
      imagePrompt(this.$prompt, this.editor, this.focusEditor);
    },
    showHtmlPrompt() {
      htmlPrompt(this.$prompt, this.editor, this.focusEditor);
    },
    showVideoPrompt() {
      videoPrompt(this.$prompt, this.editor, this.focusEditor);
    },
    showLinkPrompt() {
      linkPrompt(this.$prompt, this.editor, this.focusEditor);
    },
    insertMergeField({ id, label, fallback }) {
      this.editor.commands.insertMergeField({ id, label, fallback });
    },
  },

  beforeDestroy() {
    this.editor.destroy();
  },
};
</script>

<style lang="scss">
.editor-menu-bar .is-active {
  color: black;
}
.editor-menu-bar button {
  color: #6d6156;
  border: none;
  background: none;
  cursor: pointer;
  outline: none;
}

/* Overrides for ProseMirror styles because TipTap doesn't give us a way to add classes inside the editor */
.editor .ProseMirror {
  outline: none !important;
  min-height: 4em;

  ul {
    list-style: disc;
    padding-left: 1.1em;
  }

  ol {
    list-style: decimal;
    padding-left: 1.1em;
  }
}
</style>
